import {useArtworkCreated} from '../artwork-created/useArtworkCreated.js'
import {ArtworkProgress} from '../artwork-created/ArtworkProgress.jsx'
import RecordedChampion from '../../components/RecordedChampion.jsx'
import SchoolTeamCoverage from '../bowl/SchoolTeamCoverage.jsx'
import ChampionshipArtworkStatus from './ChampionshipArtworkStatus.jsx'
import { worklistNextWork } from './pursuit/worklistNextWork.js'
import SplitParentReview from './pursuit/SplitParentReview.jsx'
import { splitParentSummary } from './pursuit/splitParentSummary.js'
import { titleTierFor, titleTierLabel } from '../../lib/titleTierView.js'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import ChampionshipAuthorityPanel from './ChampionshipAuthorityPanel.jsx'
import { createPortal } from 'react-dom'
import {
  sportMeta, contentionMeta, pursuitMeta, formatDate, daysToEvent, formatCountdownFor, ownerName,
  dateLabelOf, dateQualifier, dateStamp,
} from '../../lib/championships.js'
import { relatedFor, reachOf, spaceOf, SPACE_LABEL } from './relatedOpportunities.js'
import { isOrgKind, COMMUNITY_HERO, GREEK_PURSUIT, CHEER_PARTNERSHIP } from './categories.js'
import { heroMeta, canNominateHero, pursuitDealMeta } from './recognition.js'
import { partnershipMeta } from './cheer.js'
import NextStep from './pursuit/NextStep.jsx'
import { SuggestedRep, RepPicker, AssignHistory } from './pursuit/AssignRep.jsx'
import HuntNow, { canHunt } from './pursuit/HuntNow.jsx'
import CompanyTie from './pursuit/CompanyTie.jsx'
import ClearCrown from './pursuit/ClearCrown.jsx'
import { EditableRow } from './pursuit/FieldCorrect.jsx'
import { useFieldPolicy } from './pursuit/useFieldPolicy.js'
import { specDrift } from './pursuit/fieldEdit.js'
import { PursuitFieldGrid } from './pursuit/PursuitFieldCorrect.jsx'
import { usePursuitFieldPolicy } from './pursuit/usePursuitFieldPolicy.js'
import { allowsFieldEdits } from './categories.js'
import DateStamp from './pursuit/DateStamp.jsx'
import { championTrust, championIssues, sourceHost } from './pursuit/ChampionCell.jsx'
import {
  fullChampion, championYear, statusDisagreements, championEvidence,
} from './pursuit/championHeadline.js'
import TamChainPanel from './TamChainPanel.jsx'
import OpportunityContact from './OpportunityContact.jsx'
import { LastLanding } from '../../components/AgentBadge.jsx'
import styles from './ChampionshipDrawer.module.css'
import { recordDisplayContext } from './recordDisplayContext.js'
import { observeDrawerScrollInset } from './drawerScrollInset.js'

/**
 * Universal championship detail drawer — the click target for EVERY surfaced
 * championship across all Command Center views. Shows the full record + the
 * competitive status (win / loss / in-contention + stage), journey status, and
 * renderings, then closes with the action console: Create Art · Email Campaign
 * · Quote · Submit for Win. Read-only on the record; actions route to the real
 * flows (art dossier, outreach, pricing engine, crown write).
 *
 * `showActions` is FALSE outside the Championships space. Every action in the
 * console is championship-shaped — the crown write and the Champion-to-Close
 * dossier both target HubSpot object 2-64425303, which a pursuit row has no
 * record in — so the console is hidden entirely rather than offered dead.
 */
/**
 * One related record, carrying the tab it lives in.
 *
 * The SPACE badge is the point of the row. Without it "FCS Semifinals" is just
 * another name; with it, the rep knows it is on Postseason Runs and that
 * clicking will take them there — which is what makes three tabs feel like one
 * board instead of three databases.
 */
function RelatedRow({ rec, onOpen, note = '' }) {
  const space = spaceOf(rec)
  return (
    <button type="button" className={styles.relRow} onClick={() => onOpen(rec)}>
      <span className={styles.relSpace} data-space={space}>{SPACE_LABEL[space]}</span>
      <span className={styles.relName}>{rec.name || 'Untitled'}</span>
      {note && <span className={styles.relNote}>{note}</span>}
      <span className={styles.relGo}>›</span>
    </button>
  )
}

export default function ChampionshipDrawer({
  champ, now, onClose, onCreateArt, onCampaign, onQuote, onSubmitWin, onCorrect, onOpenDossier,
  showActions = true, spaceLabel = '', rowAction = null,
  /* THE SPACE ITSELF — needed because the pursuit field editor is an opt-in per
     LINE (`allowsFieldEdits`), and the line is what selects the field policy.
     Defaults null, so a caller that doesn't pass it gets exactly the drawer it
     had before and no editor at all. */
  category = null,
  /* `{ gate, onAssign, busyId }` — the Manage Assignments panel. Null outside
     the Championships space, so no other drawer grows a rep picker. */
  assign = null,
  /* THE CROSS-TAB THREAD. `universe` is the FULL championship plane, which
     `useCategoryUniverse` already holds in memory in every space — so relating
     records costs one pass and no request. `onOpenRelated` re-points the drawer
     at a record that may live in a DIFFERENT tab; both default to inert, so a
     caller that doesn't pass them gets exactly the drawer it had before. */
  universe = null, onOpenRelated = null,
  /* WITHDRAWING AN UNSOURCED CROWN. Championship-space only for the same reason
     every other write here is — it targets HubSpot 2-64425303 — so it rides
     `showActions`. `onCrownCleared` repaints the board from what the server
     confirmed it wrote; `readDegraded` is the universe's own read quality,
     which decides whether this record can be judged at all. Both default
     inert, so a caller that doesn't pass them gets the drawer it had before. */
  onCrownCleared = null, readDegraded = false,
  /* CORRECTING A FIELD ON THE RECORD. `onFieldEdited` repaints the board from
     the field the server CONFIRMED it wrote and the re-read agreed with —
     nothing else. Defaults inert, so a caller that doesn't pass it gets the
     drawer it had before. */
  onFieldEdited = null, initialTask = null, campaignWorkspace = null, onWorkspaceSection = null,
}) {
  /* ⚠️ THE HOOK GOES ABOVE `if (!champ) return null`, AND THAT IS NOT STYLE.
   * A `useFieldPolicy()` placed after the early return is a CONDITIONAL HOOK —
   * `react-hooks/rules-of-hooks` — and `npm run build` fails on it with exit
   * code 2. CLAUDE.md records exactly this: a `useMemo` added below a
   * permission early-return took a deploy down, and the verify suites and a
   * bare `vite build` both passed while it was broken. */
  const hasCampaignWorkspace=Boolean(campaignWorkspace)
  const artworkRead = useArtworkCreated(champ?.sourceType || champ?.isIndyGroup ? null : champ?.id)
  const fieldPolicy = useFieldPolicy()
  const [localDrawerView, setLocalDrawerView] = useState(initialTask === 'research' && showActions ? 'actions' : 'record')
  const drawerView=campaignWorkspace ? initialTask==='record'?'record':['actions','research'].includes(initialTask)?'actions':initialTask&&!initialTask.startsWith('campaign')?'record':'campaign' : localDrawerView
  const setDrawerView=value=>{if(campaignWorkspace&&onWorkspaceSection)onWorkspaceSection(value);else setLocalDrawerView(value)}
  const taskTarget = useRef(null)
  const headerTarget = useRef(null)
  const drawerInset = useRef(null)
  useLayoutEffect(() => {
    const dispose = observeDrawerScrollInset(taskTarget.current, headerTarget.current)
    drawerInset.current = dispose
    return () => { if (drawerInset.current === dispose) drawerInset.current = null; dispose() }
  }, [champ?.id])
  // Artwork status can commit between the observer's last sample and app focus.
  // Synchronize this drawer before paint; retain the observer's user-intent guard.
  useLayoutEffect(() => { drawerInset.current?.sync?.() })
  useEffect(() => {
    if (!initialTask) {
      const frame = requestAnimationFrame(() => taskTarget.current?.querySelector('button[aria-label="Close"]')?.focus())
      return () => cancelAnimationFrame(frame)
    }
    setLocalDrawerView(initialTask === 'research' && showActions ? 'actions' : 'record')
    if(hasCampaignWorkspace&&initialTask.startsWith('campaign'))return undefined
    const frame = requestAnimationFrame(() => {
      const target = taskTarget.current?.querySelector('[data-work-section="' + (initialTask === 'research' && showActions ? 'research' : ['preparation', 'undated'].includes(initialTask) ? 'date' : 'champion') + '"]')
      drawerInset.current?.sync?.()
      target?.focus({preventScroll:true}); target?.scrollIntoView({block:'start'})
    })
    return () => cancelAnimationFrame(frame)
  }, [initialTask, showActions, champ?.id,hasCampaignWorkspace])

  /* ⚠️ SAME RULE, SECOND HOOK — unconditional, and above the early return.
   *
   * The LINE is the argument rather than a condition on the call: a space that
   * does not opt in passes '', and `usePursuitFieldPolicy` idles on a blank
   * line instead of requesting one. Cached PER LINE, because the eight lines do
   * not agree — RFP is `deals` with a different field set and a different
   * `never` list, so one shared policy would draw five company fields over a
   * deal. */
  const pursuitPolicy = usePursuitFieldPolicy(
    allowsFieldEdits(category) ? String(category?.line || '') : '',
  )

  if (!champ) return null

  /* THE PURSUIT FIELD EDITOR'S GATE. Three clauses, and they fail in different
   * directions on purpose:
   *   allowsFieldEdits(category)  the SPACE opted in (four lines today)
   *   champ.sourceType            the RECORD is a pursuit row, not a championship
   *   pursuitPolicy?.ok           the policy LOADED — a failed read removes the
   *                               controls rather than drawing inputs that 403 */
  const pursuitEditsOn = allowsFieldEdits(category)
  const editCategory = pursuitEditsOn ? { ...category, allowsFieldEdits: true } : null
  const pursuitEdits = pursuitEditsOn && Boolean(champ.sourceType) && pursuitPolicy?.ok === true

  const sport = sportMeta(champ.sport)
  const cont = contentionMeta(champ)
  const pm = pursuitMeta(champ.pursuit)
  const displayContext = recordDisplayContext(champ)
  const days = daysToEvent(champ, now)
  const splitParent = splitParentSummary(champ)
  const hasChampion = !splitParent && Boolean(champ.champion || champ.winningSchool)
  // AN ORGANIZATION IS NOT AN EVENT. A corporate account, a police or fire
  // department and a Greek chapter all run ongoing programs: there is no sport,
  // no event date, no competitive status and nobody to crown, so those blocks
  // are absent rather than rendered as "Unspecified" / "Not captured" on every
  // record. A championship record carries no `kind`, so its drawer is untouched.
  const recognition = isOrgKind(champ.kind)
  // …and WHICH organization program this one runs, which decides the headline
  // number, the record grid and the console's primary action.
  const heroKind = champ.kind === COMMUNITY_HERO
  const greekKind = champ.kind === GREEK_PURSUIT
  // THE CHEER GYM. Two panels: where it is on the partnership pipeline (both
  // halves, separately) and its championship footprint — the titles its teams
  // have won, which is the hierarchy this space exists to surface.
  const gymKind = champ.kind === CHEER_PARTNERSHIP
  const partnership = gymKind ? partnershipMeta(champ) : null
  const hero = heroMeta(champ)
  const heroGate = heroKind ? canNominateHero(champ) : { allowed: false, reason: '' }
  const pursuit = pursuitDealMeta(champ)
  const titleRouting = titleTierFor(champ)
  const titleCategory = titleTierLabel(champ)
  const nextWork = worklistNextWork(champ, now)
  const journeyStep = ['sourced','unsourced'].includes(nextWork.kind) ? 3 : champ.pursuit === 'Won' ? 2 : cont.key === 'contention' ? 1 : 0
  const journeyLabel = artworkRead.state==='conflict'&&artworkRead.rows?.length?'Saved artwork · workspace reconciliation needed':artworkRead.row?.headline || nextWork.next

  const val = (x, fallback = '—') => (x && String(x).trim() ? x : <span className={styles.gap}>{fallback}</span>)

  /* ---- WHY THIS RECORD IS THIS REP'S ----------------------------------
   *
   * ⚠️ 9,774 assignments were written to production on 2026-09-30, every one
   * carrying a written reason, and until this block existed not one of them
   * reached a screen. The field was absent from the frontend AND from the
   * backend's read list, so the answer to "why is this mine" was sitting on
   * every record and was unreachable from the product.
   *
   * It is READ-ONLY on purpose. The control that SETS an assignment already
   * exists and already collects its own reason ("Why this rep? (required)") —
   * this is the record of what was decided, not a second place to decide it.
   * Two writers on one field is how a ledger stops being trustworthy.
   *
   * NO ROW IS DEFAULTED, and every blank says which blank it is. A reason that
   * renders as an em-dash is indistinguishable from a reason nobody wrote, and
   * the whole point of the field is that assignment is not arbitrary.
   */
  const AssignmentRows = () => {
    const support = [champ.supportRep, champ.supportRep2].filter((s) => s && String(s).trim())
    return (
      <>
        {/* ⚠️ ALL THREE SEATS, AS PEERS. `sr_support_rep_2` is in the backend
            write allowlist (OPERATIONAL_FIELDS, live in f57be91), so the third
            seat is an ordinary part of a team and is rendered as one. It was
            briefly shown with a "not set from this screen" caveat on the
            strength of the comment at assignmentPlan.js:40 — which says the
            field is in no tier and was true before 2026-09-30. Reading that
            comment as the current state of production is the stale-fact failure
            this release is also guarding against. */}
        {support.length > 0 && (
          <Row k="Also assigned">{support.join(' · ')}</Row>
        )}
        {champ.assignCycle && <Row k="Held for">{champ.assignCycle}</Row>}
        {champ.assignedAt && <Row k="Assigned on">{formatDate(champ.assignedAt)}</Row>}
        <Row k="Why this rep">
          {champ.assignReason
            ? champ.assignReason
            : (
              <span className={styles.gap}>
                No reason recorded on this record — assignments made from this screen
                record one, so a blank here is an assignment that predates that.
              </span>
            )}
        </Row>
        {champ.assignLocked && (
          <Row k="Settled by a person">
            Yes
            <span className={styles.est} title="A person has settled this assignment, so an automated pass will not move it.">
              automated passes leave it alone
            </span>
          </Row>
        )}
      </>
    )
  }

  /* ---- A ROW THAT CAN CORRECT ITSELF ----------------------------------
   *
   * ⚠️ CHAMPIONSHIPS SPACE ONLY, AND IT IS GATED IN ONE PLACE. `champion-update`
   * targets HubSpot 2-64425303, which no pursuit row has a record in — an RFP,
   * a police department and a corporate account would all 404 or, worse, hit
   * some other object's id. So `ER` collapses to the plain read-only `Row`
   * outside the Championships space, which means every call site below is
   * automatically correct and no future row can forget the check.
   *
   * `showActions` is the existing space flag (false outside Championships) and
   * `champ.sourceType` is the pursuit-adapter marker — the same pair `canHunt`
   * and `canClearCrown` test. Both, because they fail in different directions:
   * the flag is what the PAGE knows, the marker is what the RECORD knows.
   */
  const canCorrect = showActions && !champ.sourceType && !recognition

  /* A surfaced field the policy no longer lists — i.e. a server-side rename.
   * `tierOfField` already returns `unknown` and `mayEditField` already refuses
   * to draw it, so nothing breaks; this is how a person finds out rather than
   * discovering it when a correction silently stops being offered. */
  const drift = canCorrect && fieldPolicy ? specDrift(fieldPolicy) : []

  const artworkRows = artworkRead.rows?.length ? artworkRead.rows : artworkRead.row ? [artworkRead.row] : []
  const artworkUnavailable = ['error','conflict'].includes(artworkRead.state)
  const related = universe && onOpenRelated ? relatedFor(champ, universe) : null
  const reach = related ? reachOf(related, champ) : 0

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <aside
        className={`${styles.panel} ${campaignWorkspace?styles.campaignPanel:''}`}
        onClick={(e) => e.stopPropagation()}
        ref={taskTarget}
        role="dialog"
        aria-label={champ.name}
        onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); onClose() } }}
        aria-modal="true"
        style={{ '--sport': sport.color }}
      >
        {/* Header */}
        <div className={styles.head} ref={headerTarget}>
          <div className={styles.headTop}>
            <h2 className={styles.title}>{champ.name}</h2>
            <button className={styles.close} onClick={onClose} aria-label="Close">✕</button>
          </div>
          <div hidden={Boolean(campaignWorkspace)&&drawerView==='campaign'}>
          {!champ.sourceType && !champ.isIndyGroup && champ.id != null && <p role="status" aria-label="Artwork work status">{artworkRows.some(row=>row.artwork?.selected) ? 'Artwork saved' : artworkRows.length ? 'Existing artwork work' : artworkUnavailable ? 'Artwork status unavailable' : artworkRead.state === 'ready' ? 'No saved original observed' : 'Reading artwork status…'}{artworkRead.state === 'conflict' ? ' · Reconciliation needed' : ''}</p>}
          {champ.titleTier && <p role="status"><strong>{titleCategory}.</strong> {titleRouting.titleTierReason}</p>}
          {/* WHO LANDED THE LAST CROWN OR CHANGE - Jarvis (Claude), Dot
              (ChatGPT), a person, or Unstamped. Server-stamped; read from the
              ledger, never inferred here (Brian 2026-10-26). */}
          {champ.id != null && <LastLanding recordId={champ.id} />}
          <div className={styles.pills}>
            {/* Win / loss / in-contention is an EVENT axis — meaningless for an
                organization that never competes. */}
            {!recognition && (
              <span className={styles.pill} style={{ color: cont.color, background: cont.color + '18', borderColor: cont.color + '55' }}>
                <span className={styles.pillDot} style={{ background: cont.color }} />
                {cont.label}
              </span>
            )}
            <span className={styles.pill} style={{ color: pm.color, background: pm.color + '14', borderColor: pm.color + '44' }}>
              {!champ.sourceType && 'Sales pursuit: '}{champ.stageLabel || pm.label}
            </span>
            {!recognition && (
              <span className={`${styles.pill} ${styles.sportPill}`}>
                <span className={styles.pillDot} style={{ background: sport.color }} />
                {sport.label}
              </span>
            )}
            {/* THE HEADLINE FACT, one per organization program: whether the
                department has a Community Hero, what product is being pursued
                at a Greek org, or how many people a corporation employs. Each
                takes the slot the countdown holds on an event record. */}
            {heroKind ? (
              <span
                className={styles.countdown}
                title={hero.present ? 'The department’s Community Hero nomination' : 'No Community Hero nominated yet'}
                style={{ color: hero.present ? hero.color : undefined }}
              >
                {hero.present
                  ? `★ ${hero.label}${hero.name ? ` · ${hero.name}` : ''}`
                  : 'No Community Hero yet'}
              </span>
            ) : greekKind ? (
              <span className={styles.countdown} style={{ color: pursuit.present ? pursuit.color : undefined }}>
                {pursuit.present ? `${champ.product || pursuit.label}` : 'No pursuit opened'}
              </span>
            ) : gymKind ? (
              <span className={styles.countdown} style={{ color: partnership.color }} title={partnership.hint}>
                {partnership.label}
                {champ.titleCount ? ` · ♛ ${champ.titleCount}` : ''}
              </span>
            ) : recognition ? (
              <span className={styles.countdown} title={champ.employeesLabel || 'Total employees'}>
                {champ.employees != null
                  ? `${champ.employees.toLocaleString()} employees`
                  : 'Headcount unknown'}
              </span>
            ) : (
              <span className={styles.countdown}>
                {/* The date is NAMED only where it isn't the event date — the
                    championship pill reads exactly as it always did. */}
                {champ.effDate
                  ? `${champ.dateKind ? dateLabelOf(champ) + ': ' : ''}${formatDate(champ.effDate)} · ${formatCountdownFor(champ, days)}`
                  : 'No date'}
                {/* ⚠️ EVERY unconfirmed date is qualified, not just an
                    estimated one. This read `champ.dateEstimated ? ' (est)'`
                    until 2026-09-20, and 1,957 records carrying only a
                    projected date have `dateEstimated === false` — so they
                    printed a bare date and a countdown as though someone had
                    published them. `dateQualifier` is empty ONLY for a
                    confirmed date. */}
                {dateQualifier(champ)}
              </span>
            )}
          </div>
          {showActions && !splitParent && onOpenDossier && <button
            className={`${styles.dossierLink} ${styles.quickDossier}`}
            type="button"
            onClick={() => onOpenDossier(champ)}
          >Review dossier & outreach →</button>}
          </div>
        </div>

        <div className={styles.drawerTabs} role="tablist" aria-label="Record sections">
          {campaignWorkspace&&<button role="tab" aria-selected={drawerView==='campaign'} onClick={()=>setDrawerView('campaign')}>Campaign workspace</button>}
          <button role="tab" aria-selected={drawerView === 'record'} onClick={() => setDrawerView('record')}>Record & evidence</button>
          <button role="tab" aria-selected={drawerView === 'actions'} onClick={() => setDrawerView('actions')}>Actions & assignment</button>
        </div>
        {campaignWorkspace&&drawerView==='campaign'&&campaignWorkspace}
        <div className={styles.body} hidden={drawerView !== 'record'}>
          {(artworkRows.length > 0 || artworkUnavailable) && <section aria-label="Saved artwork status details">
            {artworkRows.map(row=><ArtworkProgress key={row.jobId} row={row} compact />)}
            {artworkUnavailable && <p role="status">Artwork status unavailable; saved artwork may exist. Refresh the read rather than recreate it.</p>}
          </section>}
          {/* Hidden record content must not duplicate the campaign contact owner. */}
          {showActions && !recognition && drawerView !== 'campaign' && <OpportunityContact record={champ} />}
          {/* ═══ THE CHAMPION. FIRST, AND WITH ROOM. ═══════════════════════
            *
            * Brian, with a screenshot: *"When I open a championship this is the
            * tiny location of the summary. Fix this."* The champion was the
            * LAST of five small stat cards plus one line of prose, under a
            * full-width gold Create Art button — the most important fact on the
            * record rendered as the smallest thing on the screen, with the
            * Action Console owning the fold. His standing instruction is
            * *"you should see the actual championships crowned"*.
            *
            * ⚠️ AND THE DRAWER RENDERED NONE OF `championIssues()`. The board
            * rows have carried the whole flag set all along — no source, shared
            * citation, cannot seed a ring, no roster. The one surface a person
            * opens specifically to judge a crown showed none of them. That is
            * the same shape as the sourceless-crown bug fixed in the Summary
            * section below: the flag fired correctly on the row, and the place
            * somebody decides said nothing. Making the layout bigger without
            * surfacing the flags would only have enlarged the wrong fact.
            *
            * Championship-shaped records only. A police department, a Greek
            * chapter, a cheer gym and a corporation never crown anybody, and
            * each already has its own headline panel directly below. */}
          {!recognition && !heroKind && !greekKind && !gymKind && (
            <section className={styles.champHead} data-sec="champion" data-work-section="champion" tabIndex={-1} aria-label="Winner evidence and split results">
              <RecordedChampion record={champ} showNext={artworkRead.row?.artwork?.state !== 'saved'} />
              {splitParent || champ.isIndyGroup ? <SplitParentReview record={champ} universe={universe} onOpen={onOpenRelated} /> : (() => {
                const name = fullChampion(champ)
                const yr = championYear(champ)
                const trust = championTrust(champ)
                const issues = championIssues(champ).map(issue => issue.key === 'nocoach'
                  ? { ...issue, label: 'historical coach not recorded', why: 'This is a historical championship fact gap, not a claim that current school contacts are unavailable. Review current contacts separately above.' }
                  : issue)
                const conflicts = statusDisagreements(champ)
                const evidence = championEvidence(champ)
                return (
                  <>
                    <div className={styles.champHeadTop}>
                      <span className="eyebrow">Champion</span>
                      {/* THE VERDICT, NEXT TO THE NAME IT JUDGES. `championTrust`
                          is the same reader the board rows use, so the drawer
                          cannot reach a different conclusion from the row that
                          led here. */}
                      <span className={styles.champTrust} data-tone={trust.tone} title={trust.why}>
                        {trust.label}
                      </span>
                    </div>

                    {name ? (
                      /* ⚠️ NO TRUNCATION AND NO `title` CRUTCH. Brian: *"I told
                         you not to shorten anything."* The name wraps to as many
                         lines as it needs — `check-no-truncation.mjs` keeps a
                         closed baseline of ellipsis sites and this is not going
                         to become one. A tooltip is not a substitute for a name
                         being readable: it does not exist on a phone, which is
                         the screen the screenshot came from. */
                      <h3 className={styles.champName}>{name}</h3>
                    ) : (
                      <p className={styles.champNone}>
                        <b>No champion recorded.</b> Nobody has been crowned on this record yet — so
                        there is nothing here to take to a customer, and nothing to build a ring from.
                      </p>
                    )}

                    {/* ⚠️ THE YEAR, BECAUSE A CROWN WITHOUT ONE IS UNCHECKABLE.
                        Two seasons of the same championship share a name, so
                        the name alone identifies no event. The BASIS is stated
                        with it: most of this object's dates are projected, and
                        a year taken off a guess must not read as a fact. */}
                    {name && (
                      yr
                        ? <p className={yr.trusted ? styles.champYear : styles.champYearSoft}>
                            <b>{yr.year}</b> <span className={styles.champDim}>— from {yr.basis}</span>
                          </p>
                        : <p className={styles.champYearSoft}>
                            <b>No year on this record.</b> Nothing here says which season this crown
                            belongs to, so it cannot be checked against a source or engraved.
                          </p>
                    )}

                    {/* THE RECEIPT, OR ITS ABSENCE — stated either way. 63 crowns
                        exist with no source at all and they must not look like
                        verified ones. */}
                    {name && (
                      champ.championSourceUrl
                        ? <p className={styles.champSrc}>
                            <a href={champ.championSourceUrl} target="_blank" rel="noopener noreferrer">
                              Source: {sourceHost(champ.championSourceUrl)}
                            </a>
                          </p>
                        : <p className={styles.champNoSrc}>
                            <b>No legacy source link on this CRM record.</b>{' '}
                            {artworkRead.row ? 'Winner verification is separate from the saved artwork and its preparation evidence. Review the evidence without recreating the artwork.' : 'Verify the recorded winner against an official source before treating this claim as confirmed.'}
                          </p>
                    )}

                    {/* What makes a crown credible, each fragment labelled.
                        Omitted entirely when the record answers none of them —
                        six "—" rows make a thin record look populated. */}
                    {evidence.length > 0 && (
                      <dl className={styles.champEvidence}>
                        {evidence.map((e) => (
                          <div key={e.k} className={styles.champEvidenceRow}>
                            <dt>{e.k}</dt>
                            <dd>{e.v}</dd>
                          </div>
                        ))}
                      </dl>
                    )}

                    {/* ⚠️ REPORTED, NOT RESOLVED. Brian asked whether `Champion`
                        and `To Pursue` showing at once was correct state or two
                        fields disagreeing. It can be either and this UI is not
                        entitled to pick — choosing one hides a defect at the
                        source. Same ruling, and deliberately the same voice, as
                        StatePanel's flag-versus-name report. */}
                    {conflicts.map((c) => (
                      <p key={c.key} className={styles.champConflict} role="alert">
                        <b>{c.label}.</b> {c.why}
                      </p>
                    ))}

                    {/* THE FLAGS THE ROW HAS ALWAYS CARRIED AND THIS DRAWER NEVER
                        SHOWED. `bad` first: a record that cannot seed a ring is
                        not four cosmetic gaps, it is a wasted afternoon in
                        artwork. Each states the consequence, not the field. */}
                    {issues.length > 0 && (
                      <ul className={styles.champIssues}>
                        {[...issues].sort((a, b) =>
                          (b.severity === 'bad') - (a.severity === 'bad')
                          || (b.severity === 'warn') - (a.severity === 'warn')
                        ).map((i) => (
                          <li key={i.key} className={styles.champIssue} data-sev={i.severity || 'quiet'}>
                            <b>{i.label}</b>
                            {i.why ? <span className={styles.champDim}> — {i.why}</span> : null}
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )
              })()}
            </section>
          )}

          {/* Status. For a recognition account the two things that matter are
              the size of the program and how warm the pursuit is — not a
              competitive standing and a champion it will never have. */}
          {!champ.sourceType && <ChampionshipAuthorityPanel champ={champ}/>}

          {heroKind ? (
            /* THE COMMUNITY HERO PANEL — the whole point of a police / fire
               space. Read straight off the associated hero deal; a department
               with none says so, and says the slot is open. */
            <div className={styles.statusRow}>
              <div className={styles.statCard} style={{ '--tone': hero.present ? hero.color : 'var(--danger)' }}>
                <div className={styles.statK}>Community Hero</div>
                <div className={styles.statV}>
                  {hero.present
                    ? (hero.name || hero.label)
                    : <span className={styles.gap}>Not nominated</span>}
                </div>
                <div className={styles.statSub}>
                  {hero.present
                    ? `${hero.label}${hero.years != null ? ` · ${hero.years} years of service` : ''}`
                    : 'No nomination deal associated to this department yet'}
                </div>
              </div>
              <div className={styles.statCard} style={{ '--tone': pm.color }}>
                <div className={styles.statK}>Nomination slot</div>
                <div className={styles.statV}>{heroGate.allowed ? 'Open' : 'Filled'}</div>
                <div className={styles.statSub}>
                  {heroGate.allowed
                    ? 'One hero per precinct — this department can be nominated'
                    : 'One hero per precinct — already taken'}
                </div>
              </div>
            </div>
          ) : greekKind ? (
            /* THE PURSUIT PANEL — product pursued, and whether it landed. */
            <div className={styles.statusRow}>
              <div className={styles.statCard} style={{ '--tone': pursuit.present ? pursuit.color : 'var(--line-strong)' }}>
                <div className={styles.statK}>Product pursued</div>
                <div className={styles.statV}>
                  {champ.product || <span className={styles.gap}>None</span>}
                </div>
                <div className={styles.statSub}>
                  {pursuit.present ? pursuit.label : 'No pursuit deal associated to this organization yet'}
                </div>
              </div>
              <div className={styles.statCard} style={{ '--tone': champ.wonDate ? '#2f9e5f' : 'var(--line-strong)' }}>
                <div className={styles.statK}>Won</div>
                <div className={styles.statV}>
                  {champ.wonDate ? formatDate(champ.wonDate) : <span className={styles.gap}>Not won</span>}
                </div>
                <div className={styles.statSub}>
                  {champ.pursuitDate ? `Pursuit opened ${formatDate(champ.pursuitDate)}` : 'Pursuit date not recorded'}
                </div>
              </div>
            </div>
          ) : gymKind ? (
            /* THE GYM PARTNERSHIP PANEL. Both halves get their own card,
               because a signed contract on its own is NOT a partnership — and
               the championship footprint gets the third, because that is what
               makes the gym worth signing in the first place. */
            <div className={styles.statusRow}>
              <div className={styles.statCard} style={{ '--tone': partnership.signed ? '#2f9e5f' : 'var(--line-strong)' }}>
                <div className={styles.statK}>Contract</div>
                <div className={styles.statV}>
                  {partnership.signed
                    ? (partnership.signedAt ? formatDate(partnership.signedAt) : 'Signed')
                    : <span className={styles.gap}>{partnership.deal ? 'Not signed' : 'No pursuit open'}</span>}
                </div>
                <div className={styles.statSub}>
                  {partnership.deal
                    ? `${partnership.stageLabel || 'Partnership deal'}${partnership.openedAt ? ` · opened ${formatDate(partnership.openedAt)}` : ''}`
                    : 'No partnership deal associated to this gym yet'}
                </div>
              </div>
              <div className={styles.statCard} style={{ '--tone': partnership.jewelry.live ? '#2f9e5f' : 'var(--line-strong)' }}>
                <div className={styles.statK}>Exclusive branded jewelry</div>
                <div className={styles.statV}>
                  {partnership.jewelry.live
                    ? 'Live'
                    : <span className={styles.gap}>{partnership.jewelry.present ? partnership.jewelry.label : 'Not launched'}</span>}
                </div>
                <div className={styles.statSub}>
                  {partnership.jewelry.live
                    ? 'The gym has its own exclusive, fully-branded program'
                    : 'The second half of a partnership — not delivered yet'}
                </div>
              </div>
              <div className={styles.statCard} style={{ '--tone': champ.titleCount ? '#a5762a' : 'var(--line-strong)' }}>
                <div className={styles.statK}>Championship relationships</div>
                <div className={styles.statV}>
                  {champ.titleCount
                    ? champ.titleCount.toLocaleString()
                    : <span className={styles.gap}>None matched</span>}
                </div>
                <div className={styles.statSub}>
                  {champ.titleCount
                    ? `across ${champ.teamCount} team${champ.teamCount === 1 ? '' : 's'}${champ.lastTitleAt ? ` · last ${formatDate(champ.lastTitleAt)}` : ''}`
                    : 'no cheer title has been matched to this gym'}
                </div>
              </div>
            </div>
          ) : recognition ? (
            <div className={styles.statusRow}>
              <div className={styles.statCard} style={{ '--tone': 'var(--line-strong)' }}>
                <div className={styles.statK}>{champ.employeesLabel || 'Total employees'}</div>
                <div className={styles.statV}>
                  {champ.employees != null
                    ? champ.employees.toLocaleString()
                    : <span className={styles.gap}>Unknown</span>}
                </div>
                <div className={styles.statSub}>
                  {champ.employees != null ? 'numberofemployees on record' : 'headcount not on record yet'}
                </div>
              </div>
              <div className={styles.statCard} style={{ '--tone': pm.color }}>
                <div className={styles.statK}>{champ.levelLabel || 'Pursuit level'}</div>
                <div className={styles.statV}>
                  {champ.pursuitLevel || <span className={styles.gap}>Not set</span>}
                </div>
                <div className={styles.statSub}>
                  {champ.pursuitLevel ? 'hs_lead_status on record' : 'lead status not set — backfill pending'}
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.statusRow}>
              <div className={styles.statCard} style={{ '--tone': cont.color }}>
                <div className={styles.statK}>Competitive position</div>
                <div className={styles.statV}>{cont.label}</div>
                <div className={styles.statSub}>{cont.stage}</div>
              </div>
              <div className={styles.statCard} style={{ '--tone': hasChampion ? '#2f9e5f' : 'var(--line-strong)' }}>
                {/* ⚠️ THE "CHAMPION CAPTURED" CARD IS GONE, AND THAT IS THE FIX
                  RATHER THAN AN OMISSION. It rendered the champion as the last
                  of several small stat cards — precisely the inverted hierarchy
                  Brian screenshotted. The champion is now the headline section
                  at the top of the body, carrying the year, the trust verdict,
                  the source, the result detail and every flag. Keeping this card
                  too would leave two places stating one fact at two sizes, and
                  the smaller would be the one that drifted. No gate asserted it. */}
              <div className={styles.statK}>Journey</div>
                <div className={styles.statV}>{journeyLabel}</div>
                <div className={styles.statSub}>{artworkRead.row ? 'Saved artwork and outreach work; winner evidence is reviewed separately below' : 'Derived from the champion and pursuit status on the record'}</div>
              </div>
            </div>
          )}

          {/*
            * THE SUMMARY — the line a rep reads before they pick up the phone.
            *
            * Brian: "I want a summary per champion included. You are already
            * verifying, it should take another 10 seconds to get this."
            *
            * It is composed from the facts the hunt already captured and
            * refused to crown without a source for — so every clause traces to
            * the record and to the receipt linked under it. Nothing is written.
            *
            * A crowned record with nothing but a name says SO, rather than
            * printing a padded sentence that implies more was captured than
            * was. That distinction is the difference between a rep opening a
            * call with a real fact and opening it with our own filler.
            */}
          {hasChampion && (
            /* `data-sec` is an unhashed hook so the phone layout can lift the
               summary to the top of the sheet — it is the line a rep opens the
               drawer FOR, and on a 375px screen it was the fourth thing down. */
            <section className={styles.section} data-sec="summary">
              <div className={styles.sectionHead}>
                <span className="eyebrow">Summary</span>
                {champ.summary?.source === 'written' && <span className={styles.est}>written by hand</span>}
              </div>
              {champ.summary?.text ? (
                <p className={styles.summaryLine}>{champ.summary.text}</p>
              ) : (
                <p className={styles.summaryGap}>
                  Champion captured, but the source gave nothing beyond the name — no score,
                  runner-up, season record, venue or coach. Those are the inputs a custom ring is
                  built from, so this one needs a look before it goes to artwork.
                </p>
              )}
              {champ.championSourceUrl ? (
                <p className={styles.summarySrc}>
                  <a href={champ.championSourceUrl} target="_blank" rel="noopener noreferrer">
                    The source this came from
                  </a>
                </p>
              ) : (
                /* ⚠️ THE ABSENCE WAS INVISIBLE HERE, WHICH IS PART OF WHY FOUR
                 * BAD CROWNS SURVIVED A DAY.
                 *
                 * This block rendered the source link when there WAS one and
                 * nothing at all when there was not — so the drawer for a
                 * sourceless crown looked like the drawer for a sourced one,
                 * minus a line nobody counts. The row's `no source` flag was
                 * firing correctly the whole time; the place a person opens to
                 * decide whether a champion is right said nothing.
                 *
                 * A missing receipt is a louder fact than a present one, so it
                 * is stated, and the control that acts on it is directly under
                 * it. */
                <p className={styles.summaryGap}>
                  <b>No source on file.</b> Nothing on this record supports the champion — no link,
                  no receipt. A crown with no source is not a crown: it cannot be trusted into
                  artwork, and nobody downstream can check it.
                </p>
              )}

              {/* ⚠️ THE CONTROL GOES WHERE THE PROBLEM IS VISIBLE, not in the
               * action console below. The console is the list of things to do
               * NEXT with a record — art, quote, campaign, crown. This is not a
               * next step; it is the answer to the sentence directly above it,
               * and separating the two puts a screenfull of record grid between
               * the evidence and the correction.
               *
               * Championship-space only (`showActions`): a pursuit row has no
               * record in 2-64425303 to correct. */}
              {showActions && (
                <ClearCrown
                  /* Keyed by record — the drawer is reused when the operator
                     opens a different championship, and without this the panel
                     would keep the previous record's confirm state open over a
                     different champion's name. */
                  key={champ.id}
                  record={champ}
                  onCleared={onCrownCleared}
                  readDegraded={readDegraded}
                />
              )}
            </section>
          )}

          {/* Full record */}
          <section className={styles.section} data-work-section="date" tabIndex={-1} aria-label="Event date and preparation facts">
            <div className={styles.sectionHead}><span className="eyebrow">Record</span></div>
            <div className={styles.grid}>
              {/* THE EMPLOYEE-RECOGNITION RECORD. What sizes, qualifies,
                  locates and reaches a corporate account — in place of the
                  sport / division / gender / conference / season set, none of
                  which a corporation has. */}
              {recognition ? (
                <>
                  {/* WHAT KIND OF BODY IT IS — a national organization vs one
                      of its chapters, a police vs a fire department. Only shown
                      where HubSpot actually records it. */}
                  {champ.orgType && <Row k="Organization type">{val(champ.orgType)}</Row>}
                  {/* The corporate qualifiers, each rendered only where the
                      space records it: a police department has no industry and
                      no headcount, and blank rows for both would be noise. */}
                  {champ.industry && <Row k={champ.industryLabel || 'Industry'}>{val(champ.industry)}</Row>}
                  {champ.employees != null && (
                    <Row k={champ.employeesLabel || 'Total employees'}>{champ.employees.toLocaleString()}</Row>
                  )}
                  <Row k={champ.hqLabel || 'Location'}>{val(champ.hqLocation, 'No location on record')}</Row>
                  <Row k="State">
                    {champ.state && champ.state !== 'NATIONAL'
                      ? champ.state
                      : <span className={styles.gap}>Not recorded</span>}
                  </Row>
                  {/* THE COMMUNITY HERO ROWS — the honoree and their years of
                      service, straight off the nomination deal. */}
                  {heroKind && (
                    <>
                      <Row k="Community Hero">
                        {hero.present
                          ? <>{hero.name || hero.label}{hero.name && <span className={styles.est}>{hero.label}</span>}</>
                          : <span className={styles.gap}>Not nominated — the slot is open</span>}
                      </Row>
                      <Row k="Years of service">
                        {hero.years != null
                          ? `${hero.years} years`
                          : <span className={styles.gap}>{hero.present ? 'Not recorded on the nomination' : '—'}</span>}
                      </Row>
                      {champ.hero?.nominatedAt && (
                        <Row k="Nominated">{formatDate(champ.hero.nominatedAt)}</Row>
                      )}
                    </>
                  )}
                  {/* THE GREEK PURSUIT ROWS — product, opened, won. */}
                  {greekKind && (
                    <>
                      <Row k={champ.productLabel || 'Product pursued'}>
                        {val(champ.product, 'No pursuit opened')}
                      </Row>
                      <Row k="Pursuit date">
                        {champ.pursuitDate
                          ? formatDate(champ.pursuitDate)
                          : <span className={styles.gap}>Not recorded</span>}
                      </Row>
                      <Row k="Won date">
                        {champ.wonDate
                          ? formatDate(champ.wonDate)
                          : <span className={styles.gap}>Not won</span>}
                      </Row>
                    </>
                  )}
                  {/* THE CHEER GYM ROWS — the partnership's two halves, the
                      teams the gym runs, and its championship footprint. The
                      TEAM COUNT ON RECORD and the teams WITH TITLES are stated
                      separately: they are different numbers and conflating them
                      would overstate the footprint. */}
                  {gymKind && (
                    <>
                      <Row k="Partnership stage">{partnership.label}</Row>
                      <Row k="Contract">
                        {partnership.signed
                          ? <>Signed{partnership.signedAt && <span className={styles.est}>{formatDate(partnership.signedAt)}</span>}</>
                          : <span className={styles.gap}>{partnership.deal ? 'Pursuit open, not signed' : 'No pursuit open'}</span>}
                      </Row>
                      <Row k="Exclusive branded jewelry">
                        {partnership.jewelry.live
                          ? 'Live'
                          : <span className={styles.gap}>{partnership.jewelry.present ? partnership.jewelry.label : 'Not launched'}</span>}
                      </Row>
                      <Row k="Teams on record">
                        {champ.gymTeamCount != null
                          ? champ.gymTeamCount
                          : <span className={styles.gap}>Not recorded on the gym</span>}
                      </Row>
                      <Row k="Teams with titles">
                        {champ.teamCount ? champ.teamCount : <span className={styles.gap}>None matched</span>}
                      </Row>
                      <Row k="Championship relationships">
                        {champ.titleCount
                          ? <>{champ.titleCount}{champ.titleBasis && <span className={styles.est}>matched by {champ.titleBasis}</span>}</>
                          : <span className={styles.gap}>No cheer title matched to this gym</span>}
                      </Row>
                    </>
                  )}
                  {champ.pursuitLevel && (
                    <Row k={champ.levelLabel || 'Pursuit level'}>{val(champ.pursuitLevel)}</Row>
                  )}
                  <Row k="Status">{val(champ.stageLabel || champ.pursuit)}</Row>
                  <Row k="Assigned rep">{ownerName(champ) || <span className={styles.gap}>Unassigned</span>}</Row>
                  {champ.schoolTeamCoverage && <Row k="School-team coverage"><SchoolTeamCoverage record={champ}/></Row>}
                  <AssignmentRows />
                  <Row k={champ.websiteLabel || 'Website'}>
                    {champ.website ? (
                      <a className={styles.sourceLink} href={champ.website} target="_blank" rel="noopener noreferrer">
                        {champ.website}
                      </a>
                    ) : (
                      <span className={styles.gap}>No website on this record</span>
                    )}
                  </Row>
                </>
              ) : (
                <>
                  {/* ⚠️ THE NAME IS THE MOST CONSEQUENTIAL FIELD ON THE RECORD
                      AND IT WAS NOT IN THIS GRID AT ALL — it was only the
                      drawer's heading, which is not a thing you can correct.
                      This is the field that reaches an engraving, and the field
                      a champion's name leaked into on the NASCAR record. It
                      goes first. */}
                  <EditableRecordRow canCorrect={canCorrect} record={champ} policy={fieldPolicy} onEdited={onFieldEdited} k="Championship name" field="championship_name">{val(champ.name)}</EditableRecordRow>
                  <EditableRecordRow canCorrect={canCorrect} record={champ} policy={fieldPolicy} onEdited={onFieldEdited} k="Sport" field="sr_sport">
                    <span className={styles.sportDot} style={{ background: sport.color }} />
                    {val(champ.sport, 'Unspecified')}
                  </EditableRecordRow>
                  <EditableRecordRow canCorrect={canCorrect} record={champ} policy={fieldPolicy} onEdited={onFieldEdited} k="Level" field="sr_level">{val(champ.level)}</EditableRecordRow>
                  <EditableRecordRow canCorrect={canCorrect} record={champ} policy={fieldPolicy} onEdited={onFieldEdited} k="Division" field="sr_segment">{val(champ.segment)}</EditableRecordRow>
                  <Row k="Gender">{val(champ.gender)}</Row>
                  <EditableRecordRow canCorrect={canCorrect} record={champ} policy={fieldPolicy} onEdited={onFieldEdited} k="Conference" field="sr_conference">{val(champ.conference)}</EditableRecordRow>
                  <Row k="Location">{champ.state !== 'NATIONAL' ? [champ.city, champ.state].filter(Boolean).join(', ') || champ.state : <span className={styles.gap}>National / unspecified</span>}</Row>
                  {/* ⚠️ STATE IS ITS OWN ROW, SEPARATE FROM LOCATION, BECAUSE
                      LOCATION IS DERIVED. `normalizeState()` reads `sr_state`
                      and falls back to `sr_state_location`, so the Location row
                      above can read correctly while `sr_state` holds the wrong
                      code — which is the live state of both CIF phantoms (MA
                      and PA on California records). Correcting what Location
                      displays would edit the wrong property. */}
                  <EditableRecordRow canCorrect={canCorrect} record={champ} policy={fieldPolicy} onEdited={onFieldEdited} k="State (sr_state)" field="sr_state">
                    {val(champ.state === 'NATIONAL' ? '' : champ.state, 'Not set — falls back to the location')}
                  </EditableRecordRow>
                  <EditableRecordRow canCorrect={canCorrect} record={champ} policy={fieldPolicy} onEdited={onFieldEdited} k="Season start year" field="sr_season_year">{val(champ.season)}</EditableRecordRow>
                  <Row k="Assigned rep">{ownerName(champ) || <span className={styles.gap}>Unassigned</span>}</Row>
                  {champ.schoolTeamCoverage && <Row k="School-team coverage"><SchoolTeamCoverage record={champ}/></Row>}
                  <AssignmentRows />
                </>
              )}
              {/* ⭐ THE MATH, on the record a rep is actually looking at. Loaded
                  lazily per record so opening a drawer costs one request and
                  closing it costs nothing. Every guard — null never rendered as
                  $0, the ceiling never promoted to a headline, the archetype
                  named as the weak link, $288 labelled a forward price — lives
                  in lib/tamChain.js, which is pure and asserted 18 ways in
                  verify-tam-chain.mjs. */}
              {champ.id && !champ.sourceType && <TamChainPanel recordId={champ.id} />}
              {/* BOTH DATES for a record that has two. The open date is the day
                  the engine SURFACED the posting — the issuer's real open date
                  isn't captured anywhere yet, so it is labelled as the proxy it
                  is rather than passed off as the solicitation's open date. */}
              {champ.openDate && (
                <Row k={champ.openDateLabel || 'Opened'}>
                  {formatDate(champ.openDate)}
                  {champ.openDateIsProxy && (
                    <span className={styles.est} title="Scan date — when the RFP engine surfaced this posting. The issuer's open date is not captured yet.">
                      proxy · {champ.openDateField || 'scan date'}
                    </span>
                  )}
                </Row>
              )}
              {/* No date row at all for a recognition account — there is no
                  event, so "Event date: No date" would be noise on every
                  single record. */}
              {!recognition && (
                <Row k={champ.sourceType ? dateLabelOf(champ) : 'Display date (effective)'}>
                  {champ.effDate
                    ? <>{formatDate(champ.effDate)}{champ.dateEstimated && <span className={styles.est}>est</span>}{champ.dateKind === 'close' && <span className={styles.est}>{formatCountdownFor(champ, days)}</span>}</>
                    : <span className={styles.gap}>No date</span>}
                  {' '}<DateStamp c={champ} />
                </Row>
              )}
              {!champ.sourceType && <>
                <Row k="Sales pursuit (sr_pursuit_status)">{displayContext.pursuit}</Row>
                <Row k="Champion outcome (sr_champion_status)">{displayContext.outcome}</Row>
                <Row k="Display date basis">{displayContext.basis} · Confidence: {displayContext.confidence}</Row>
                <Row k="Stored confirmed date (sr_confirmed_date)">{displayContext.confirmed}</Row>
                <Row k="Stored event date (sr_event_date)">{displayContext.event}</Row>
                <Row k="Stored projected date (sr_projected_date)">{displayContext.projected}</Row>
                <p>Sales pursuit and champion outcome are separate fields. The display date uses confirmed, then event, then projected input, with existing season/date checks. These labels do not change any stored value.</p>
              </>}
              {/* ⚠️ THE DRAWER IS WHERE A REP DECIDES, so it says in words
                  whether the date is a fact or the system's own estimate.
                  A stamp alone answers "is this sourced?"; only this answers
                  "so what do I do about it?" — and the difference matters most
                  for the ~90% that are still projected. */}
              {!recognition && champ.effDate && (
                <Row k="Date provenance">
                  {dateStamp(champ)?.kind === 'sourced'
                    ? <span className={styles.provOk}>Read from this governing body’s own published championship calendar.</span>
                    : <span className={styles.provEst}>Projected — derived from the season, not read off a calendar. The hunt fires on this date, so treat the countdown as an estimate.</span>}
                </Row>
              )}
              {/* ⚠️ `sr_event_date` IS NOT THE ROW ABOVE, AND CONFLATING THEM
                  WOULD BE A SILENT NO-OP. The row above shows `effDate`, which
                  the normalizer derives as confirmed ?? event ?? projected — so
                  a record whose `sr_confirmed_date` is right and whose
                  `sr_event_date` is wrong displays the right date and fires the
                  hunt on the wrong one. That is the live state of the NASCAR
                  record: confirmed 2026-09-06, event 2026-09-20.

                  So the raw property gets its own row, labelled with its own
                  name, showing what IT holds — and `currentValue()` reads the
                  raw property rather than the derived one for the same reason. */}
              {canCorrect && (
                <>
                  <EditableRecordRow canCorrect={canCorrect} record={champ} policy={fieldPolicy} onEdited={onFieldEdited} k="Event date (sr_event_date)" field="sr_event_date">
                    {champ.raw?.sr_event_date
                      ? String(champ.raw.sr_event_date).slice(0, 10)
                      : <span className={styles.gap}>Not set</span>}
                  </EditableRecordRow>
                  <EditableRecordRow canCorrect={canCorrect} record={champ} policy={fieldPolicy} onEdited={onFieldEdited} k="Date status" field="sr_date_status">{val(champ.dateStatus)}</EditableRecordRow>
                  <div className={styles.dateSourceRow}>
                    <EditableRecordRow canCorrect={canCorrect} record={champ} policy={fieldPolicy} onEdited={onFieldEdited} k="Date source" field="sr_date_source"><span className={styles.dateSourceValue}>{val(champ.raw?.sr_date_source)}</span></EditableRecordRow>
                  </div>
                  {/* ⚠️ THE ONE FIELD THAT CAN ONLY EVER BE SET FROM HERE.
                      The route answers `automation_forbidden` to any service
                      identity — measured, not assumed — so no engine, no grant
                      and no scheduled job can set it. Before this control the
                      only way it got set was as a SIDE EFFECT of recording a
                      champion by hand, which meant a record with NO champion
                      could never be locked at all. Both CIF phantoms are
                      exactly that record: no champion, nothing to protect them
                      from being re-crowned by the next sweep. */}
                  <EditableRecordRow canCorrect={canCorrect} record={champ} policy={fieldPolicy} onEdited={onFieldEdited} k="Champion lock" field="champion_locked">
                    {champ.championLocked
                      ? <b>Locked — automation cannot change the champion</b>
                      : <span className={styles.gap}>Not locked</span>}
                  </EditableRecordRow>
                </>
              )}
              {drift.length > 0 && (
                <div className={styles.cell}>
                  <div className={styles.k}>Field policy</div>
                  <div className={styles.v}>
                    <span className={styles.gap}>
                      {drift.join(', ')} {drift.length === 1 ? 'is' : 'are'} no longer in any tier the
                      server reports, so {drift.length === 1 ? 'it' : 'they'} cannot be corrected here.
                      Likely renamed server-side.
                    </span>
                  </div>
                </div>
              )}
              {/* Pursuit-only identity — blank on a championship, so none of
                  these rows exist there. */}
              {champ.sourceType && !recognition && (
                <>
                  <Row k={champ.productLabel || 'Product category'}>{val(champ.product, 'Uncategorized')}</Row>
                  <Row k="Status">{val(champ.stageLabel || champ.pursuit)}</Row>
                  {champ.issuer && <Row k="Issuing agency">{val(champ.issuer)}</Row>}
                  {champ.refNumber && <Row k="Solicitation number">{val(champ.refNumber)}</Row>}
                  {champ.brand && <Row k="Brand">{val(champ.brand)}</Row>}
                </>
              )}
              {/* The incumbent is the rival RING VENDOR on a championship —
                  a concept a corporate recognition account doesn't have. */}
              {!recognition && (
                <>
                  <Row k="Incumbent (ring vendor)">
                    {champ.incumbent
                      ? <>{champ.incumbent}{champ.incumbentConfidence && <span className={styles.est}>{champ.incumbentConfidence}</span>}</>
                      : <span className={styles.gap}>Unknown — needs a rep to fill it in</span>}
                  </Row>
                  {champ.incumbentSource && <Row k="Incumbent source">{val(champ.incumbentSource)}</Row>}
                </>
              )}
              {/* Pursuit records only (a championship record has no
                  `sourceType`), so the championship drawer is unchanged. The
                  recognition grid above already shows the company's website
                  under its own name, so it isn't repeated here. */}
              {champ.sourceType && !recognition && (
                <Row k="Official listing">
                  {champ.sourceUrl ? (
                    <>
                      <a
                        className={styles.sourceLink}
                        href={champ.sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {champ.sourceUrl}
                      </a>
                      {champ.sourceUrlField && (
                        <span className={styles.est} title="Which property on the record carried this URL">
                          {champ.sourceUrlField}
                        </span>
                      )}
                    </>
                  ) : (
                    <span className={styles.gap}>No submission link on this record</span>
                  )}
                </Row>
              )}

              {/* ⚠️ CORRECT THIS FIELD, ON A PURSUIT RECORD — the other half of
                  "these all must have read, and write functionality."

                  It sits in the RECORD GRID, not the action console, for the
                  same reason the championship editor does: the console is the
                  list of things to do NEXT with a record; this is "this value
                  is wrong", and the answer has to be next to the question.

                  ⚠️ AND IT DRAWS NO ROW LIST OF ITS OWN. `PursuitFieldGrid` is
                  `pursuitEditableFields(policy, record)` — the server's fields,
                  in the server's order, with the portal's own labels and enum
                  options. Hand-writing rows here would recreate `FIELD_SPECS`
                  in JSX, which is the exact thing this build set out not to
                  duplicate. A tier change server-side reaches this grid with no
                  edit here.

                  Gated on the SPACE (`allowsFieldEdits`, an opt-in per line —
                  four lines today, each measured live) AND on the RECORD's own
                  `sourceType` marker, because the two fail in different
                  directions: the flag is what the PAGE knows, the marker is
                  what the RECORD knows. A championship carries no `sourceType`,
                  so this can never draw over one. */}
              {pursuitEdits && (
                <PursuitFieldGrid
                  record={champ}
                  policy={pursuitPolicy}
                  category={editCategory}
                  onEdited={onFieldEdited}
                />
              )}
            </div>
          </section>

          {/* Journey + renderings. The journey is the PRE-WIN → CROWNED →
              artwork path: it only exists for a record that can be won, so a
              recognition account gets no phantom progress bar stuck at step
              one and no ring-artwork prompt. */}
          {!recognition && (
            <section className={styles.section}>
              <div className={styles.sectionHead}><span className="eyebrow">Journey status</span></div>
              <div className={styles.journey}>
                {[0, 1, 2, 3].map((i) => (
                  <span key={i} className={`${styles.jstep} ${i <= journeyStep ? styles.jstepOn : ''}`} />
                ))}
              </div>
              <div className={styles.jlabel}>{journeyLabel}</div>
              <div style={{ marginTop: 10 }}>
                <div className={styles.k}>Renderings</div>
                <div className={styles.v} style={{ color: 'var(--muted)' }}>
                  {showActions ? <ChampionshipArtworkStatus record={champ} onOpenDossier={onOpenDossier}/> : 'Artwork is reviewed on the linked championship record.'}
                </div>
              </div>
            </section>
          )}

          {/* ═══ RELATED OPPORTUNITIES — the thread between the tabs ═══
              Brian: "integration between every single tab is Paramount."

              Splitting Bowl Season and Postseason Runs into their own spaces
              made each legible and, on its own, made one school's season
              unreadable — scattered across three tabs that could not see each
              other. This is the drawer's answer: from a title, every postseason
              round beneath it; from a round, the title it leads to and its
              sibling rounds. Each row says which SPACE it lives in and opens it
              in place, so a rep never has to remember which tab a record is on.

              It renders only when there IS kin — an empty "Related" heading on
              every one of 14k records is noise, not integration. */}
          {related && (related.rounds.length > 0 || related.siblings.length > 0
            || related.title || related.ambiguousTitle) && (
            <section className={styles.section}>
              <div className={styles.sectionHead}>
                <span className="eyebrow">Related opportunities</span>
                {reach > 0 && (
                  <span className={styles.k} style={{ textTransform: 'none' }}>
                    {/* The number that says why a round is worth working: a
                        title is one customer, its bracket is up to this many. */}
                    reaches up to {reach} schools
                  </span>
                )}
              </div>

              {related.title && (
                <RelatedRow rec={related.title} onOpen={onOpenRelated}
                  note="the title this run leads to" />
              )}

              {/* Two candidate titles is an AMBIGUITY, shown rather than
                  resolved by picking the first. A wrong parent sends a rep to
                  the wrong championship, which is worse than no link. */}
              {related.ambiguousTitle && (
                <div className={styles.v} style={{ color: 'var(--muted)', marginBottom: 8 }}>
                  {related.ambiguousTitle.length} championships match this lineage —
                  linking none rather than guessing which is the parent.
                </div>
              )}

              {related.rounds.map((r) => (
                <RelatedRow key={r.id} rec={r} onOpen={onOpenRelated} />
              ))}
              {related.siblings.map((r) => (
                <RelatedRow key={r.id} rec={r} onOpen={onOpenRelated} />
              ))}
            </section>
          )}
        </div>

        <div hidden={drawerView !== 'actions'}>
        {/* ACTION CONSOLE — championship-space only. Every action below writes
            against (or builds from) HubSpot 2-64425303; a pursuit record has no
            row there, so outside that space the console is replaced by an
            explicit read-only note rather than dead buttons. */}
        {!showActions && (
          <div className={styles.console}>
            <div className={styles.consoleTitle}>
              {rowAction
                ? rowAction.consoleTitle
                : champ.sourceUrl ? (recognition ? 'Research the account' : 'Participate') : 'Read-only'}
            </div>

            {/* THE GUIDED NEXT STEP, first in the console — the single move
                this record is waiting on, numbered against the space's
                sequence, with what happens when you click it. The full action
                list below it stays available; this is what says which one to
                reach for. */}
            {rowAction && <NextStep record={champ} action={rowAction} />}

            {/* THE RECOGNITION / PURSUIT ACTION — "Nominate Community Hero" on a
                department, "Start pursuit" on a Greek organization. This is the
                one action a read-only space offers that isn't just a link, and
                one-hero-per-precinct is enforced right here: a department that
                already has an active nomination gets the reason, not a button
                that would create a second hero. */}
            {rowAction && (
              <div className={styles.submitRow}>
                <button
                  type="button"
                  className={styles.submitAction}
                  disabled={rowAction.kind === 'hero' && !heroGate.allowed}
                  style={rowAction.kind === 'hero' && !heroGate.allowed
                    ? { opacity: 0.5, cursor: 'not-allowed' }
                    : undefined}
                  onClick={() => {
                    if (rowAction.kind === 'hero' && !heroGate.allowed) return
                    rowAction.onStart?.(champ)
                  }}
                >
                  {rowAction.label}
                </button>
                <span className={styles.submitHint}>
                  {rowAction.kind === 'hero'
                    ? (heroGate.allowed
                      ? `Opens the nomination flow for a ${rowAction.honoreeNoun} at this department. SR provides the ring or pendant at no cost.`
                      : heroGate.reason)
                    : 'Opens a product pursuit against this organization — product, assigned rep and the date it opened.'}
                </span>
              </div>
            )}
            {/* THE ONE ACTION a read-only space does offer: the official
                channel to participate. An RFP with no path to submit is
                useless, so this is the drawer's primary CTA when present. */}
            {champ.sourceUrl && (
              <div className={styles.submitRow}>
                <a
                  className={styles.submitAction}
                  href={champ.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {champ.submitLabel || 'Open official listing'} ↗
                </a>
                <span className={styles.submitHint}>
                  {recognition
                    ? 'Opens the company’s own site in a new tab — the Command Center never writes to it.'
                    : 'Opens the official channel in a new tab — submissions happen there, not here.'}
                </span>
              </div>
            )}
            <div className={styles.readOnlyNote}>
              {champ.sourceType && !champ.sourceUrl && (
                <><strong>No {recognition ? 'website' : 'submission link'} on this record.</strong>{' '}</>
              )}
              {/* ⚠️ THE WORD "READ-ONLY" IS NO LONGER TRUE OF EVERY PURSUIT
                  SPACE, AND SAYING IT ANYWAY WOULD BE THE WRONG KIND OF STALE.
                  A space with the field editor on can correct this record's
                  fields right here; what it still cannot do is the CHAMPIONSHIP
                  writes, which target an object it has no row in. Those are two
                  different statements and collapsing them would teach a rep
                  that the grid above does nothing. */}
              {pursuitEdits ? (
                <>
                  {spaceLabel ? `The ${spaceLabel} space ` : 'This space '}
                  corrects record fields above — with a reason, to the ledger, read back to confirm.
                  Crown, Submit-for-Win and the Champion-to-Close dossier are championship writes
                  against HubSpot 2-64425303 and are not offered here.
                </>
              ) : (
                <>
                  {spaceLabel ? `The ${spaceLabel} space is ` : 'This space is '}read-only. Crown, Submit-for-Win and the
                  Champion-to-Close dossier are championship writes against HubSpot 2-64425303 and are not offered here.
                </>
              )}
            </div>
          </div>
        )}
        {/* THE ASSIGNMENT PANEL — who owns this, who the win history says
            should, and every change made to it. Above the action console
            because "who is on this" is the question a rep asks first.
            The suggestion renders only for an UNOWNED record: the engine does
            not second-guess a human's assignment. The picker is the write, and
            it is drawn only for someone holding Manage Assignments. */}
        {assign && (
          <div className={styles.assignPanel}>
            <div className={styles.consoleTitle}>Rep assignment</div>
            <div className={styles.assignRow}>
              <span className={styles.assignOwner}>
                {ownerName(champ) || <span className={styles.gap}>Unassigned</span>}
              </span>
              <RepPicker
                /* Keyed by record: the drawer is reused when the operator opens
                   a different championship, and without this the picker would
                   keep the previous record's pre-selected rep. */
                key={champ.id}
                record={champ}
                gate={assign.gate}
                onAssign={assign.onAssign}
                busy={assign.busyId === champ.id}
              />
            </div>
            {assign.error && (
              <div className={styles.assignError} role="status">{assign.error}</div>
            )}
            <SuggestedRep record={champ} />
            <AssignHistory recordId={champ.id} />
          </div>
        )}
        {showActions && !splitParent && (
        <div className={styles.console}>
          <div className={styles.consoleTitle}>Action console</div>
          <div className={styles.actions}>
            <button className={`${styles.action} ${styles.primaryAction}`} onClick={() => onCreateArt?.(champ)}>
              <span className={styles.actionIcon} style={{ background: 'transparent' }}><ArtIcon /></span>
              <span className={styles.actionText}><b>Create Art</b><span>Assemble the ring proof from the owned brand kit</span></span>
            </button>
            <button className={styles.action} onClick={() => onCampaign?.(champ)}>
              <span className={styles.actionIcon} style={{ background: '#3f6f9a' }}><MailIcon /></span>
              <span className={styles.actionText}><b>Email Campaign</b><span>Draft the closing sequence</span></span>
            </button>
            <button className={styles.action} onClick={() => onQuote?.(champ)}>
              <span className={styles.actionIcon} style={{ background: '#4a8f6e' }}><TagIcon /></span>
              <span className={styles.actionText}><b>Quote</b><span>Open the pricing engine, pre-filled</span></span>
            </button>
            <button className={styles.action} onClick={() => onSubmitWin?.(champ)}>
              <span className={styles.actionIcon} style={{ background: '#2f9e5f' }}><CrownIcon /></span>
              <span className={styles.actionText}><b>{splitParent ? 'Review split results' : 'Review winner evidence'}</b><span>Sourced resolution required; no crown is written here</span></span>
            </button>

            {/* ⚠️ CORRECT THE RECORD — the door that was missing.
              *
              * `CrownModal` has had a `mode="edit"` branch since it shipped, its
              * own header says that branch calls `api.championUpdate`, and
              * CLAUDE.md documents field-correction as a real path. Nothing
              * ever rendered it: CommandCenter passed `mode="crown"` and only
              * ever `mode="crown"`, so `api.championUpdate` was unreachable and
              * a wrong pursuit status could not be fixed from anywhere in the
              * app. Found by `check-doors.mjs` on its first run.
              *
              * It sits BELOW Submit for Win deliberately. Crowning is the
              * forward move and should stay the obvious one; correcting is the
              * repair, and a repair offered as prominently as the main action
              * invites people to reach for it first. */}
            {/* WHO IS THE CUSTOMER. Sits inside the console because it is the
              * step BETWEEN a champion and every commercial action beside it —
              * Art, Quote and Email all assume somebody knows which account
              * this is, and until now nothing in the app answered that. */}
            {onCorrect && <CompanyTie record={champ} />}

            {onCorrect && (
              <button className={styles.action} onClick={() => onCorrect(champ)}>
                <span className={styles.actionIcon} style={{ background: '#8a6d3b' }}><TagIcon /></span>
                <span className={styles.actionText}>
                  <b>Correct this record</b>
                  <span>Fix the pursuit status or the assigned rep — no champion is written</span>
                </span>
              </button>
            )}

            {/* HUNT THE CHAMPION — the step BEFORE Submit for Win.
              *
              * Brian: "we should be able to pushbutton it and pull and record."
              *
              * Everything else in this console assumes the champion is already
              * known. This is the one that establishes it — in the app: it runs
              * the real hunt, shows who won and the source it came from, and
              * Record writes it to the championship.
              *
              * It replaced a control that copied a prompt and opened ChatGPT,
              * which left the app and recorded nothing. */}
            {!canHunt(champ) && initialTask === 'research' && <p data-work-section="research" tabIndex={-1}>Research is unavailable for this record. Review its record status before continuing.</p>}
            {canHunt(champ) && (
              <div className={styles.action} data-work-section="research" tabIndex={-1} aria-label="Research options. No research runs until requested.">
                <span className={styles.actionIcon} style={{ background: '#6c5ce7' }}><SparkIcon /></span>
                <span className={styles.actionText}>
                  <b>Hunt the champion</b>
                  <span>Search, show the source, and record it — without leaving</span>
                  <HuntNow record={champ} />
                </span>
              </div>
            )}
          </div>
        </div>
        )}
        </div>
      </aside>
    </div>,
    document.body
  )
}

/* inline action icons */
const ic = { width: 17, height: 17, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.7, strokeLinecap: 'round', strokeLinejoin: 'round' }
const ArtIcon = () => (<svg {...ic}><circle cx="12" cy="12" r="9" /><circle cx="12" cy="8" r="1.2" /><circle cx="8" cy="12" r="1.2" /><circle cx="16" cy="12" r="1.2" /><path d="M12 21a2.5 2.5 0 0 0 0-5 1.6 1.6 0 0 1 0-3" /></svg>)
const MailIcon = () => (<svg {...ic}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>)
const TagIcon = () => (<svg {...ic}><path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0l-7.2-7.2A2 2 0 0 1 2.8 12V4a1 1 0 0 1 1-1h8a2 2 0 0 1 1.4.6l7.4 7.4a2 2 0 0 1 0 2.4Z" /><circle cx="7.5" cy="7.5" r="1.2" /></svg>)
const CrownIcon = () => (<svg {...ic}><path d="M4 8l3.5 3L12 5l4.5 6L20 8l-1.5 10h-13Z" /></svg>)
const SparkIcon = () => (<svg {...ic}><path d="M12 3.2 13.7 9l5.8 1.7-5.8 1.7L12 18.2l-1.7-5.8L4.5 10.7 10.3 9Z" /></svg>)

// This component must have stable identity. Defining it in the drawer remounted
// FieldCorrect on every parent render and discarded in-flight/save results.
function EditableRecordRow({ canCorrect, record, policy, onEdited, k, field, children }) {
  return canCorrect ? <EditableRow k={k} field={field} record={record} policy={policy} onEdited={onEdited}>{children}</EditableRow> : <Row k={k}>{children}</Row>
}

const Row = ({ k, children }) => (
    <div className={styles.cell}>
      <div className={styles.k}>{k}</div>
      <div className={styles.v}>{children}</div>
    </div>
  )

